import React from 'react';
import { ImageIcon, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const BrandingManager = ({ branding, setBranding, onSave }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Shop-Identität
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="shop-name">Shop-Name</Label>
            <Input
              id="shop-name"
              value={branding.shopName}
              onChange={(e) => setBranding({ ...branding, shopName: e.target.value })}
              placeholder="Homeyoursoul"
            />
          </div>
          
          <div>
            <Label htmlFor="tagline">Slogan / Tagline</Label>
            <Textarea
              id="tagline"
              value={branding.tagline}
              onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
              placeholder="Dein Online-Shop für..."
              rows={2}
            />
          </div>
          
          <div>
            <Label htmlFor="logo-url">Logo URL</Label>
            <Input
              id="logo-url"
              value={branding.logo}
              onChange={(e) => setBranding({ ...branding, logo: e.target.value })}
              placeholder="https://..."
            />
            <p className="text-xs text-gray-600 mt-1">Bild-URL Ihres Logos</p>
          </div>
          
          <div>
            <Label htmlFor="favicon">Favicon URL (optional)</Label>
            <Input
              id="favicon"
              value={branding.favicon}
              onChange={(e) => setBranding({ ...branding, favicon: e.target.value })}
              placeholder="https://..."
            />
            <p className="text-xs text-gray-600 mt-1">Icon für Browser-Tab</p>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <span className="text-lg">📱</span>
              Social Media Links
            </h3>
            
            <div className="space-y-3">
              <div>
                <Label htmlFor="facebook">Facebook URL</Label>
                <Input
                  id="facebook"
                  value={branding.socialMedia?.facebook || ''}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    socialMedia: { 
                      ...branding.socialMedia, 
                      facebook: e.target.value 
                    } 
                  })}
                  placeholder="https://facebook.com/IhrShop"
                />
              </div>
              
              <div>
                <Label htmlFor="instagram">Instagram URL</Label>
                <Input
                  id="instagram"
                  value={branding.socialMedia?.instagram || ''}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    socialMedia: { 
                      ...branding.socialMedia, 
                      instagram: e.target.value 
                    } 
                  })}
                  placeholder="https://instagram.com/IhrShop"
                />
              </div>
              
              <div>
                <Label htmlFor="tiktok">TikTok URL</Label>
                <Input
                  id="tiktok"
                  value={branding.socialMedia?.tiktok || ''}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    socialMedia: { 
                      ...branding.socialMedia, 
                      tiktok: e.target.value 
                    } 
                  })}
                  placeholder="https://tiktok.com/@IhrShop"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-600 mt-2">
              Diese Links erscheinen im Footer mit Icons
            </p>
          </div>
          
          <Button onClick={onSave} className="w-full">
            <Check className="h-4 w-4 mr-2" />
            Branding speichern
          </Button>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Vorschau</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border rounded-lg p-6 bg-white">
              {branding.logo && (
                <img
                  src={branding.logo}
                  alt="Logo"
                  className="h-16 w-auto mb-3"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <h2 className="text-2xl font-bold mb-2">{branding.shopName || 'Shop-Name'}</h2>
              <p className="text-gray-600 mb-4">{branding.tagline || 'Ihre Tagline hier...'}</p>
              
              {/* Social Media Preview */}
              {(branding.socialMedia?.facebook || branding.socialMedia?.instagram || branding.socialMedia?.tiktok) && (
                <div className="flex gap-2 pt-3 border-t">
                  {branding.socialMedia?.facebook && (
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center" title="Facebook">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                  )}
                  {branding.socialMedia?.instagram && (
                    <div className="w-8 h-8 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 rounded-full flex items-center justify-center" title="Instagram">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                  )}
                  {branding.socialMedia?.tiktok && (
                    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center" title="TikTok">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandingManager;
